import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  useEmpleadoEnfermedadesLaborales,
  useRegistrarEnfermedadLaboral,
  usePatchEnfermedadLaboral,
  useEliminarEnfermedadLaboral,
  furelDocumentoUrl,
  type EnfermedadLaboral,
  type EnfermedadLaboralEstado,
} from '@/hooks/sgsst/incidentes/use-enfermedad-laboral'
import {downloadBlob} from '@/lib/api'
import {localToday, formatDateOnly} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const EL_ESTADOS: EnfermedadLaboralEstado[] = ['en_calificacion', 'calificada', 'controvertida']

function elEstadoTone(estado: EnfermedadLaboralEstado): 'green' | 'amber' | 'neutral' {
  if (estado === 'calificada') return 'green'
  if (estado === 'controvertida') return 'amber'
  return 'neutral'
}

/** Registra el hecho calificado; el detalle clínico no se guarda en Kairos (custodia de salud). */
function EnfermedadLaboralDialog({
  businessId,
  employeeId,
  el,
  trigger,
}: {
  businessId: string
  employeeId: string
  el?: EnfermedadLaboral
  trigger: React.ReactNode
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [fecha, setFecha] = useState(el?.fecha_diagnostico ?? '')
  const [estado, setEstado] = useState<EnfermedadLaboralEstado>(el?.estado_calificacion ?? 'en_calificacion')
  const [entidad, setEntidad] = useState(el?.entidad_calificadora ?? '')
  const [reportada, setReportada] = useState(el?.reportada_externamente ?? false)
  const [radicado, setRadicado] = useState(el?.radicado ?? '')
  const [notas, setNotas] = useState(el?.notas ?? '')
  const [fechaError, setFechaError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const registrar = useRegistrarEnfermedadLaboral(businessId, employeeId)
  const patch = usePatchEnfermedadLaboral(businessId, employeeId)
  const pending = registrar.isPending || patch.isPending

  function reset() {
    setFecha(el?.fecha_diagnostico ?? '')
    setEstado(el?.estado_calificacion ?? 'en_calificacion')
    setEntidad(el?.entidad_calificadora ?? '')
    setReportada(el?.reportada_externamente ?? false)
    setRadicado(el?.radicado ?? '')
    setNotas(el?.notas ?? '')
    setFechaError(undefined)
    setError(null)
  }

  function blurFecha() {
    if (!el) setFechaError(validateRequired(fecha) ?? undefined)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!el) {
      const err = validateRequired(fecha)
      setFechaError(err ?? undefined)
      if (err) return
    }
    setError(null)
    const onSuccess = () => setOpen(false)
    const onError = (err: unknown) => setError(translateApiError(err))
    if (el) {
      patch.mutate(
        {
          elId: el.id,
          patch: {
            estado_calificacion: estado,
            entidad_calificadora: entidad || null,
            reportada_externamente: reportada,
            radicado: reportada ? radicado || null : null,
            notas: notas || null,
          },
        },
        {onSuccess, onError}
      )
    } else {
      registrar.mutate(
        {
          employee_id: employeeId,
          fecha_diagnostico: fecha,
          estado_calificacion: estado,
          entidad_calificadora: entidad || null,
          reportada_externamente: reportada,
          radicado: reportada ? radicado || null : null,
          notas: notas || null,
        },
        {onSuccess, onError}
      )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) reset()
      }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{el ? t('enfermedadLaboral.edit.title') : t('enfermedadLaboral.create.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground" data-testid="el-custodia-hint">
          {t('enfermedadLaboral.custodiaHint')}
        </p>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {!el && (
            <div onBlur={blurFecha}>
              <DateField
                id="el-fecha"
                label={t('enfermedadLaboral.fields.fechaDiagnostico')}
                value={fecha}
                max={localToday()}
                onChange={(value) => {
                  setFecha(value)
                  setFechaError(undefined)
                }}
              />
              <FieldError error={fechaError} />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="el-estado">{t('enfermedadLaboral.fields.estado')}</Label>
            <select
              id="el-estado"
              value={estado}
              onChange={(e) => setEstado(e.target.value as EnfermedadLaboralEstado)}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              {EL_ESTADOS.map((value) => (
                <option key={value} value={value}>
                  {t(`enfermedadLaboral.estado.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="el-entidad">{t('enfermedadLaboral.fields.entidad')}</Label>
            <Input
              id="el-entidad"
              value={entidad}
              maxLength={tope('RAZ')}
              onChange={(e) => setEntidad(e.target.value)}
              placeholder={t('enfermedadLaboral.fields.entidadPlaceholder')}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={reportada}
              onChange={(e) => setReportada(e.target.checked)}
              data-testid="el-reportada"
            />
            {t('enfermedadLaboral.fields.reportadaExternamente')}
          </label>
          {reportada && (
            <div className="space-y-2">
              <Label htmlFor="el-radicado">{t('enfermedadLaboral.fields.radicado')}</Label>
              <Input
                id="el-radicado"
                value={radicado}
                maxLength={100}
                onChange={(e) => setRadicado(e.target.value)}
                className="font-mono"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="el-notas">{t('enfermedadLaboral.fields.notas')}</Label>
            <Textarea
              id="el-notas"
              value={notas}
              maxLength={2000}
              onChange={(e) => setNotas(e.target.value)}
              placeholder={t('enfermedadLaboral.fields.notasPlaceholder')}
            />
          </div>
          <FormDialogFooter
            error={error}
            pending={pending}
            submitLabel={el ? t('common.save') : t('enfermedadLaboral.create.submit')}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function EnfermedadesLaboralesSection({
  businessId,
  employeeId,
  employeeName,
  canEdit,
}: {
  businessId: string
  employeeId: string
  employeeName: string
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useEmpleadoEnfermedadesLaborales(businessId, employeeId, page)
  const els = data?.items
  const eliminar = useEliminarEnfermedadLaboral(businessId, employeeId)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)

  if (isLoading && !data) return <Skeleton className="h-24 w-full" />
  return (
    <div className="space-y-4">
      {(!els || els.length === 0) && (
        <p className="text-sm text-muted-foreground" data-testid="empleado-el-empty">
          {t('enfermedadLaboral.empty', {name: employeeName})}
        </p>
      )}
      {els && els.length > 0 && (
        <ul className="divide-y divide-border" data-testid="empleado-el-list">
          {els.map((el) => (
            <li key={el.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                  <Tag tone={elEstadoTone(el.estado_calificacion)} data-testid={`el-estado-${el.id}`}>
                    {t(`enfermedadLaboral.estado.${el.estado_calificacion}`)}
                  </Tag>
                  {el.entidad_calificadora && <span className="text-muted-foreground">{el.entidad_calificadora}</span>}
                </p>
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                  {t('enfermedadLaboral.diagnosticadaEl', {date: formatDateOnly(el.fecha_diagnostico)})}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`el-furel-${el.id}`}
                  onClick={() => downloadBlob(furelDocumentoUrl(businessId, el.id), `furel-${el.id.slice(0, 8)}.docx`)}>
                  {t('enfermedadLaboral.descargarFurel')}
                </Button>
                {canEdit && (
                  <EnfermedadLaboralDialog
                    businessId={businessId}
                    employeeId={employeeId}
                    el={el}
                    trigger={
                      <Button variant="outline" size="sm" data-testid={`el-edit-${el.id}`}>
                        {t('enfermedadLaboral.editar')}
                      </Button>
                    }
                  />
                )}
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`el-delete-${el.id}`}
                    onClick={() => setConfirmingId(el.id)}>
                    {t('enfermedadLaboral.eliminar')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {data && data.total > 0 && (
        <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage} />
      )}
      {canEdit && (
        <EnfermedadLaboralDialog
          businessId={businessId}
          employeeId={employeeId}
          trigger={
            <Button variant="outline" size="sm" data-testid="el-registrar">
              {t('enfermedadLaboral.create.action')}
            </Button>
          }
        />
      )}
      <ConfirmDialog
        open={confirmingId !== null}
        onOpenChange={(open) => !open && setConfirmingId(null)}
        title={t('enfermedadLaboral.deleteTitle')}
        description={t('enfermedadLaboral.deleteDescription')}
        confirmLabel={t('enfermedadLaboral.eliminar')}
        cancelLabel={t('common.cancel')}
        pending={eliminar.isPending}
        onConfirm={() => {
          if (confirmingId) eliminar.mutate(confirmingId, {onSuccess: () => setConfirmingId(null)})
        }}
      />
    </div>
  )
}
